import {
  getShellThemeFromStorage,
  SHELL_THEME_DEFAULT_VARIANT,
  THEME2_ACCENT_PRESET_FIRST,
} from "./sidebarShellTheme";

export const REACT_TABLE_THEME_STORAGE_KEY = "reactTableTheme";
export const REACT_TABLE_THEME_CHANGED_EVENT = "react-table-theme-changed";

/** Default accent — matches SubHeader / `--info` when no custom color is stored. */
export const SUBHEADER_ACCENT_DEFAULT_HEX = "#0092E0";

/** Legacy Theme 2 table header default (dark gray); migrated to accent on read. */
const LEGACY_THEME2_TABLE_HEADER_BG = THEME2_ACCENT_PRESET_FIRST;

/** Resolved active theme accent hex (`sidebarCustomColor` or default blue). */
export function getActiveThemeAccentHex() {
  if (typeof window === "undefined") return SUBHEADER_ACCENT_DEFAULT_HEX;
  const stored = localStorage.getItem("sidebarCustomColor");
  if (stored && stored.trim()) {
    return normalizeHexColor(stored, SUBHEADER_ACCENT_DEFAULT_HEX);
  }
  return SUBHEADER_ACCENT_DEFAULT_HEX;
}
/** Theme 2 dark — table cell text on dark row backgrounds. */
export const REACT_TABLE_THEME2_CELL_TEXT = "#FFFFFF";
/** Theme 2 light — readable body text on white / light-gray rows. */
export const REACT_TABLE_THEME2_LIGHT_CELL_TEXT = "#3c4858";

/** @typedef {"light" | "dark"} TableThemeMode */

/**
 * @typedef {Object} ReactTableThemeColors
 * @property {string} headerBg
 * @property {string} headerText
 * @property {string} rowStripeBg
 * @property {string} rowHoverBg
 * @property {string} cellText
 * @property {string} rowBorder
 * @property {string} paginationAccent
 */

/** @type {Record<TableThemeMode, ReactTableThemeColors>} */
export const REACT_TABLE_THEME_DEFAULTS = {
  light: {
    headerBg: "#64B5F6",
    headerText: "#FFFFFF",
    rowStripeBg: "#F2F2F2",
    rowHoverBg: "#F2F2F2",
    cellText: "#525F7F",
    rowBorder: "#D1D4DC",
    paginationAccent: "#42A5F5",
  },
  dark: {
    headerBg: "#64B5F6",
    headerText: "#FFFFFF",
    rowStripeBg: "#1D1D2C",
    rowHoverBg: "#252536",
    cellText: "#B3B3B3",
    rowBorder: "#3A3A4A",
    paginationAccent: "#42A5F5",
  },
};

const CSS_VAR_HEADER_BG = "--rt-header-bg";
const CSS_VAR_HEADER_TEXT = "--rt-header-text";
const CSS_VAR_ROW_STRIPE_BG = "--rt-row-stripe-bg";
const CSS_VAR_ROW_HOVER_BG = "--rt-row-hover-bg";
const CSS_VAR_CELL_TEXT = "--rt-cell-text";
const CSS_VAR_ROW_BORDER = "--rt-row-border";
const CSS_VAR_PAGINATION_ACCENT = "--rt-pagination-accent";
/** Opaque surface for even (non-striped) rows — sticky first column only; not in Appearance UI */
const CSS_VAR_ROW_BG = "--rt-row-bg";

const ROW_BG_BY_MODE = {
  light: "#FFFFFF",
  dark: "#1E1E25",
};

const COLOR_KEYS = [
  "headerBg",
  "headerText",
  "rowStripeBg",
  "rowHoverBg",
  "cellText",
  "rowBorder",
  "paginationAccent",
];

function clampChannel(n) {
  return Math.min(255, Math.max(0, Math.round(Number(n))));
}

function channelsToHex(r, g, b) {
  const toPair = (c) => clampChannel(c).toString(16).padStart(2, "0");
  return `#${toPair(r)}${toPair(g)}${toPair(b)}`.toUpperCase();
}

/**
 * Normalizes any supported color string to `#RRGGBB` (uppercase).
 * Legacy `rgba(...)` values from storage are converted to opaque hex.
 */
export function normalizeHexColor(value, fallback = "#000000") {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return normalizeHexColor(fallback, "#000000");
  }

  const rgbaMatch = trimmed.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+)?\s*\)$/i
  );
  if (rgbaMatch) {
    return channelsToHex(rgbaMatch[1], rgbaMatch[2], rgbaMatch[3]);
  }

  let hex = trimmed.startsWith("#") ? trimmed.slice(1) : trimmed;
  if (/^[0-9A-Fa-f]{3}$/.test(hex)) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (/^[0-9A-Fa-f]{6}$/.test(hex)) {
    return `#${hex.toUpperCase()}`;
  }

  return normalizeHexColor(fallback, "#000000");
}

function isPlainObject(v) {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

/** Factory defaults — Reset button restores this (SubHeader blue, not live accent). */
export function getReactTableThemeResetDefaults(shellThemeVariant = "1") {
  const light = { ...REACT_TABLE_THEME_DEFAULTS.light };
  const dark = { ...REACT_TABLE_THEME_DEFAULTS.dark };
  if (shellThemeVariant === "2") {
    light.headerBg = SUBHEADER_ACCENT_DEFAULT_HEX;
    dark.headerBg = SUBHEADER_ACCENT_DEFAULT_HEX;
    light.cellText = REACT_TABLE_THEME2_LIGHT_CELL_TEXT;
    dark.cellText = REACT_TABLE_THEME2_CELL_TEXT;
    light.paginationAccent = SUBHEADER_ACCENT_DEFAULT_HEX;
    dark.paginationAccent = SUBHEADER_ACCENT_DEFAULT_HEX;
  }
  return { light, dark };
}

/** Runtime defaults when nothing is stored — Theme 2 header tracks live accent. */
export function getReactTableThemeDefaults(shellThemeVariant = "1") {
  const light = { ...REACT_TABLE_THEME_DEFAULTS.light };
  const dark = { ...REACT_TABLE_THEME_DEFAULTS.dark };
  if (shellThemeVariant === "2") {
    const accentHeader = getActiveThemeAccentHex();
    light.headerBg = accentHeader;
    dark.headerBg = accentHeader;
    light.cellText = REACT_TABLE_THEME2_LIGHT_CELL_TEXT;
    dark.cellText = REACT_TABLE_THEME2_CELL_TEXT;
    light.paginationAccent = accentHeader;
    dark.paginationAccent = accentHeader;
  }
  return { light, dark };
}

function mergeModeColors(mode, source, baseDefaults) {
  const defaults = baseDefaults || REACT_TABLE_THEME_DEFAULTS[mode];
  const out = { ...defaults };
  if (!isPlainObject(source)) return out;
  for (const key of COLOR_KEYS) {
    if (typeof source[key] === "string" && source[key].trim()) {
      out[key] = normalizeHexColor(source[key], defaults[key]);
    }
  }
  if (mode === "light" && normalizeHexColor(out.cellText) === "#FFFFFF") {
    out.cellText = REACT_TABLE_THEME2_LIGHT_CELL_TEXT;
  }
  if (
    getShellThemeFromStorage() === "2" &&
    normalizeHexColor(out.headerBg) === normalizeHexColor(LEGACY_THEME2_TABLE_HEADER_BG)
  ) {
    out.headerBg = getActiveThemeAccentHex();
  }
  return out;
}

/** @returns {Record<TableThemeMode, ReactTableThemeColors>} */
export function getReactTableThemeFromStorage() {
  const shellVariant = typeof window === "undefined" ? SHELL_THEME_DEFAULT_VARIANT : getShellThemeFromStorage();
  const shellDefaults = getReactTableThemeDefaults(shellVariant);
  if (typeof window === "undefined") {
    return {
      light: { ...shellDefaults.light },
      dark: { ...shellDefaults.dark },
    };
  }
  try {
    const raw = localStorage.getItem(REACT_TABLE_THEME_STORAGE_KEY);
    if (!raw) {
      return {
        light: { ...shellDefaults.light },
        dark: { ...shellDefaults.dark },
      };
    }
    const parsed = JSON.parse(raw);
    return {
      light: mergeModeColors("light", parsed?.light, shellDefaults.light),
      dark: mergeModeColors("dark", parsed?.dark, shellDefaults.dark),
    };
  } catch {
    return {
      light: { ...shellDefaults.light },
      dark: { ...shellDefaults.dark },
    };
  }
}

/** @returns {TableThemeMode} */
export function getActiveTableThemeMode() {
  if (typeof document === "undefined") return "light";
  return document.body.classList.contains("white-content") ? "light" : "dark";
}

function applyColorsToDocument(colors, mode) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.setProperty(CSS_VAR_HEADER_BG, colors.headerBg);
  root.style.setProperty(CSS_VAR_HEADER_TEXT, colors.headerText);
  root.style.setProperty(CSS_VAR_ROW_STRIPE_BG, colors.rowStripeBg);
  root.style.setProperty(CSS_VAR_ROW_HOVER_BG, colors.rowHoverBg);
  root.style.setProperty(CSS_VAR_CELL_TEXT, colors.cellText);
  root.style.setProperty(CSS_VAR_ROW_BORDER, colors.rowBorder);
  root.style.setProperty(CSS_VAR_PAGINATION_ACCENT, colors.paginationAccent);
  root.style.setProperty(
    CSS_VAR_ROW_BG,
    ROW_BG_BY_MODE[mode] ?? ROW_BG_BY_MODE.dark
  );
}

/** Applies stored colors for the current light/dark mode to `document.documentElement`. */
export function applyReactTableThemeToDocument() {
  const mode = getActiveTableThemeMode();
  const theme = getReactTableThemeFromStorage();
  applyColorsToDocument(theme[mode], mode);
}

/**
 * @param {TableThemeMode} mode
 * @param {Partial<ReactTableThemeColors>} partial
 */
export function setReactTableThemeInStorage(mode, partial, shellThemeVariant = "1") {
  if (typeof window === "undefined") return;
  const defaults = getReactTableThemeDefaults(shellThemeVariant)[mode];
  const normalizedPartial = {};
  if (isPlainObject(partial)) {
    for (const key of COLOR_KEYS) {
      if (typeof partial[key] === "string" && partial[key].trim()) {
        normalizedPartial[key] = normalizeHexColor(partial[key], defaults[key]);
      }
    }
  }
  const shellDefaults = getReactTableThemeDefaults(shellThemeVariant);
  const current = getReactTableThemeFromStorage();
  current[mode] = mergeModeColors(
    mode,
    { ...current[mode], ...normalizedPartial },
    shellDefaults[mode]
  );
  localStorage.setItem(REACT_TABLE_THEME_STORAGE_KEY, JSON.stringify(current));
  applyReactTableThemeToDocument();
  window.dispatchEvent(new Event(REACT_TABLE_THEME_CHANGED_EVENT));
}

/** Header colors that track the shell accent unless the user customized table colors. */
const ACCENT_LINKED_HEADER_HEXES = [
  SUBHEADER_ACCENT_DEFAULT_HEX,
  LEGACY_THEME2_TABLE_HEADER_BG,
  "#64B5F6",
];

function getAppliedTableHeaderBgHex() {
  if (typeof document === "undefined") return "";
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(CSS_VAR_HEADER_BG)
    .trim();
  return raw ? normalizeHexColor(raw) : "";
}

function getRawStoredTableHeaderBgHex(mode) {
  try {
    const raw = localStorage.getItem(REACT_TABLE_THEME_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const value = parsed?.[mode]?.headerBg;
    return typeof value === "string" && value.trim()
      ? normalizeHexColor(value)
      : null;
  } catch {
    return null;
  }
}

function isAccentLinkedTableHeader(headerHex, previousAccentHex) {
  const header = normalizeHexColor(headerHex);
  if (!header) return false;
  const prev = normalizeHexColor(previousAccentHex);
  if (header === prev) return true;
  return ACCENT_LINKED_HEADER_HEXES.some(
    (legacy) => normalizeHexColor(legacy) === header
  );
}

/**
 * When accent changes, update table header if it still matched the previous accent
 * (or a legacy accent default). Uses raw storage / applied CSS vars — not
 * `getActiveThemeAccentHex()` after the accent has already been saved.
 */
export function syncReactTableHeaderBgWithAccent(
  previousAccentHex,
  nextAccentHex,
  shellThemeVariant = "2"
) {
  if (typeof window === "undefined" || shellThemeVariant !== "2") return;
  const prev = normalizeHexColor(previousAccentHex);
  const next = normalizeHexColor(nextAccentHex);
  if (prev === next) return;

  const hasPersistedTableTheme = !!localStorage.getItem(REACT_TABLE_THEME_STORAGE_KEY);
  const appliedHeader = getAppliedTableHeaderBgHex();
  let changed = false;
  const current = getReactTableThemeFromStorage();

  for (const mode of /** @type {const} */ (["light", "dark"])) {
    const rawHeader = getRawStoredTableHeaderBgHex(mode);
    const effectiveHeader = rawHeader ?? appliedHeader;
    if (!isAccentLinkedTableHeader(effectiveHeader, prev)) continue;
    current[mode] = { ...current[mode], headerBg: next, paginationAccent: next };
    changed = true;
  }

  if (!changed) return;

  if (hasPersistedTableTheme) {
    localStorage.setItem(REACT_TABLE_THEME_STORAGE_KEY, JSON.stringify(current));
  }
  applyReactTableThemeToDocument();
  window.dispatchEvent(new Event(REACT_TABLE_THEME_CHANGED_EVENT));
}

/**
 * Restores table color defaults.
 * @param {TableThemeMode | null | undefined} mode Pass `null` to reset light + dark (full defaults).
 */
export function resetReactTableThemeInStorage(mode, shellThemeVariant = SHELL_THEME_DEFAULT_VARIANT) {
  if (typeof window === "undefined") return;
  const resetDefaults = getReactTableThemeResetDefaults(shellThemeVariant);
  if (!mode) {
    localStorage.setItem(
      REACT_TABLE_THEME_STORAGE_KEY,
      JSON.stringify({
        light: { ...resetDefaults.light },
        dark: { ...resetDefaults.dark },
      })
    );
  } else {
    const current = getReactTableThemeFromStorage();
    current[mode] = { ...resetDefaults[mode] };
    localStorage.setItem(REACT_TABLE_THEME_STORAGE_KEY, JSON.stringify(current));
  }
  applyReactTableThemeToDocument();
  window.dispatchEvent(new Event(REACT_TABLE_THEME_CHANGED_EVENT));
}

export { COLOR_KEYS as REACT_TABLE_THEME_COLOR_KEYS };

/**
 * Injected after page layout styles so sticky first column follows Sidebar Appearance
 * `--rt-*` tokens (loaded later in DOM than global SCSS).
 * @param {string} [scopeSelector] Optional ancestor (e.g. ".add-vehicle")
 */
export function getStickyStockTableStickyColumnCss(scopeSelector = "") {
  const scope = scopeSelector ? `${scopeSelector} ` : "";
  return `
  ${scope}.sticky-stock-table .rt-thead .rt-th:first-child,
  ${scope}.sticky-stock-table .rt-tbody .rt-td:first-child {
    position: sticky !important;
    left: 0;
    z-index: 2;
    background-image: none !important;
    border-right: 1px solid var(--rt-row-border, rgba(255, 255, 255, 0.1)) !important;
  }
  ${scope}.sticky-stock-table .rt-thead .rt-th:first-child {
    z-index: 3;
  }
`;
}
